import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { spotsApi, uploadApi, uploadUrl, type CarMakeApi } from '../../api/client';
import { CAR_TYPES, PERFORMANCE_TYPES } from '../../constants/spotCategories';
import { COUNTRIES_US_FIRST } from '../../constants/countries';
import styles from './SpotEdit.module.css';

const MAX_IMAGES = 4;
const CURRENT_MAKE_VALUE = '__current__';
const OTHER_MAKE_VALUE = '__other__';

export function SpotEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, getUsers, loadUsers } = useAuth();
  const { spots, updateSpot, removeSpot, fetchSpot } = useData();
  const users = getUsers();

  const spot = id ? spots.find((s) => s.id === id) : null;
  const isAuthor = spot && user && spot.authorId === user.id;

  const [caption, setCaption] = useState('');
  const [car, setCar] = useState({
    makeId: '',
    make: '',
    model: '',
    generation: '',
    yearRange: '',
    bodyStyle: '',
    performanceType: '',
    trim: '',
    mods: '',
    wheels: '',
    userNotes: '',
  });
  const [location, setLocation] = useState('');
  const [city, setCity] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [event, setEvent] = useState('');
  const [taggedOwnerId, setTaggedOwnerId] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [carMakes, setCarMakes] = useState<CarMakeApi[]>([]);
  const [useCustomMake, setUseCustomMake] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingImage, setAddingImage] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!id || spot) return;
    void fetchSpot(id, { incrementView: false });
  }, [fetchSpot, id, spot]);

  useEffect(() => {
    if (spot) {
      setCaption(spot.caption ?? '');
      setImageUrls(spot.images?.length ? [...spot.images] : (spot.imageUrl ? [spot.imageUrl] : []));
      setCar({
        makeId: spot.car.makeId ?? '',
        make: spot.car.make ?? '',
        model: spot.car.model ?? '',
        generation: spot.car.generation ?? '',
        yearRange: spot.car.yearRange ?? '',
        bodyStyle: spot.car.bodyStyle ?? '',
        performanceType: spot.car.performanceType ?? '',
        trim: spot.car.trim ?? '',
        mods: spot.car.mods ?? '',
        wheels: spot.car.wheels ?? '',
        userNotes: spot.car.userNotes ?? '',
      });
      setLocation(spot.location ?? '');
      setCity(spot.city ?? '');
      setCountryCode(spot.countryCode ?? '');
      setEvent(spot.event ?? '');
      setTaggedOwnerId(spot.taggedOwnerId ?? '');
      setUseCustomMake(false);
    }
  }, [spot]);

  useEffect(() => {
    let cancelled = false;
    spotsApi.carMakes()
      .then((makes) => {
        if (!cancelled) setCarMakes(makes);
      })
      .catch(() => {
        if (!cancelled) setCarMakes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedMake = car.makeId
    ? carMakes.find((make) => make.id === car.makeId)
    : carMakes.find((make) => make.name.toLowerCase() === car.make.trim().toLowerCase());
  const selectedMakeId = selectedMake?.id ?? '';
  const makeSelectValue = selectedMakeId || (car.make ? CURRENT_MAKE_VALUE : '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !spot || !isAuthor) return;
    if (!car.make || !car.model) {
      setError('Make and model are required.');
      setImageError('');
      return;
    }
    if (!car.bodyStyle || !car.performanceType) {
      setError('Please select both car type and performance type.');
      setImageError('');
      return;
    }
    if (imageUrls.length < 1 || imageUrls.length > MAX_IMAGES) {
      setImageError(`Spot must have between 1 and ${MAX_IMAGES} images.`);
      setError('');
      return;
    }
    setSubmitting(true);
    setError('');
    setImageError('');
    try {
      const updated = await updateSpot(id, {
        caption: caption || undefined,
        images: imageUrls,
        car: {
          makeId: useCustomMake ? undefined : (selectedMakeId || undefined),
          make: car.make,
          model: car.model,
          generation: car.generation || undefined,
          yearRange: car.yearRange || undefined,
          bodyStyle: car.bodyStyle || undefined,
          performanceType: car.performanceType || undefined,
          trim: car.trim || undefined,
          mods: car.mods || undefined,
          wheels: car.wheels || undefined,
          userNotes: car.userNotes || undefined,
        },
        location: location || undefined,
        city: city || undefined,
        countryCode: countryCode || undefined,
        event: event || undefined,
        taggedOwnerId: taggedOwnerId || undefined,
      });
      navigate(`/spot/${id}`, { state: { spot: updated, fromSpotForm: true } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update spot.';
      const isImageError = /image|already used|duplicate/i.test(msg);
      if (isImageError) {
        setImageError(msg);
        setError('');
      } else {
        setError(msg);
        setImageError('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    if (imageUrls.length <= 1) return;
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    setImageUrls((prev) => {
      const next = [...prev];
      const swap = direction === 'up' ? index - 1 : index + 1;
      if (swap < 0 || swap >= next.length) return prev;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
  };

  const handleAddImageClick = () => {
    if (imageUrls.length >= MAX_IMAGES) return;
    fileInputRef.current?.click();
  };

  const handleAddImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/') || imageUrls.length >= MAX_IMAGES) return;
    setAddingImage(true);
    setImageError('');
    try {
      const { files: uploaded } = await uploadApi.spot([file]);
      const filename = uploaded[0]?.filename;
      if (filename) {
        setImageUrls((prev) => [...prev, uploadUrl(filename)]);
      }
    } catch (err) {
      setImageError(err instanceof Error ? err.message : 'Failed to add image.');
    } finally {
      setAddingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !spot || !isAuthor || deleting || submitting) return;
    if (!window.confirm('Delete this spot? This cannot be undone.')) return;

    setDeleting(true);
    setError('');
    setImageError('');
    try {
      await removeSpot(id);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete spot.');
      setDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <p>Please sign in to edit spots.</p>
        <Link to="/login">Sign in</Link>
      </div>
    );
  }

  if (!spot) {
    return (
      <div className={styles.page}>
        <p>Spot not found.</p>
        <Link to="/">Back to feed</Link>
      </div>
    );
  }

  if (!isAuthor) {
    return (
      <div className={styles.page}>
        <p>You can only edit your own spots.</p>
        <Link to={`/spot/${id}`}>Back to spot</Link>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Link to={`/spot/${id}`} className={styles.back}>← Back to spot</Link>
      <h1>Edit spot</h1>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.section}>
          <h2>Images</h2>
          <p className={styles.sectionNote}>Use the arrows to reorder. At least one image required (max {MAX_IMAGES}).</p>
          <div className={styles.imageList}>
            {imageUrls.map((url, index) => (
              <div key={index} className={styles.imageItem}>
                <img src={url} alt={`Spot ${index + 1}`} className={styles.imageThumb} />
                <div className={styles.imageActions}>
                  <button
                    type="button"
                    className={styles.orderBtn}
                    onClick={() => handleMoveImage(index, 'up')}
                    disabled={index === 0}
                    title="Move earlier"
                    aria-label="Move earlier"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.orderBtn}
                    onClick={() => handleMoveImage(index, 'down')}
                    disabled={index === imageUrls.length - 1}
                    title="Move later"
                    aria-label="Move later"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className={styles.removeImage}
                    onClick={() => handleRemoveImage(index)}
                    disabled={imageUrls.length <= 1}
                    title={imageUrls.length <= 1 ? 'Keep at least one image' : 'Remove image'}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {imageUrls.length < MAX_IMAGES && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAddImageFile}
                  className={styles.hiddenInput}
                  aria-hidden
                />
                <button
                  type="button"
                  className={styles.addImageBtn}
                  onClick={handleAddImageClick}
                  disabled={addingImage}
                >
                  {addingImage ? 'Uploading...' : '+ Add image'}
                </button>
              </>
            )}
          </div>
          {imageError && <p className={styles.error}>{imageError}</p>}
        </div>
        <div className={styles.section}>
          <h2>Caption</h2>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption for your spot..."
            className={styles.input}
          />
        </div>
        <div className={styles.section}>
          <h2>Car details</h2>
          <div className={styles.grid}>
            <label>
              Make *
              {useCustomMake ? (
                <>
                  <button
                    type="button"
                    className={styles.selectMakeLink}
                    onClick={() => {
                      const matchedMake = carMakes.find((make) => make.name.toLowerCase() === car.make.trim().toLowerCase());
                      setCar((c) => ({
                        ...c,
                        makeId: matchedMake?.id ?? '',
                        make: matchedMake?.name ?? '',
                      }));
                      setUseCustomMake(false);
                    }}
                  >
                    select make
                  </button>
                  <input
                    value={car.make}
                    onChange={(e) => setCar((c) => ({ ...c, makeId: '', make: e.target.value }))}
                    required
                    placeholder="Enter car make"
                  />
                </>
              ) : (
                <select
                  value={makeSelectValue}
                  onChange={(e) => {
                    if (e.target.value === OTHER_MAKE_VALUE) {
                      setCar((c) => ({ ...c, makeId: '', make: '' }));
                      setUseCustomMake(true);
                      return;
                    }
                    if (e.target.value === CURRENT_MAKE_VALUE) return;
                    const nextMake = carMakes.find((make) => make.id === e.target.value);
                    setCar((c) => ({
                      ...c,
                      makeId: nextMake?.id ?? '',
                      make: nextMake?.name ?? '',
                    }));
                  }}
                  className={styles.select}
                  required
                >
                  <option value="">— Select make —</option>
                  {car.make && !selectedMakeId && <option value={CURRENT_MAKE_VALUE}>{car.make}</option>}
                  {carMakes.map((make) => (
                    <option key={make.id} value={make.id}>{make.name}</option>
                  ))}
                  <option value={OTHER_MAKE_VALUE}>Other</option>
                </select>
              )}
            </label>
            <label>
              Model *
              <input value={car.model} onChange={(e) => setCar((c) => ({ ...c, model: e.target.value }))} required />
            </label>
            <label>
              Year range
              <input value={car.yearRange} onChange={(e) => setCar((c) => ({ ...c, yearRange: e.target.value }))} placeholder="e.g. 2019-2023" />
            </label>
            <label>
              Generation
              <input value={car.generation} onChange={(e) => setCar((c) => ({ ...c, generation: e.target.value }))} placeholder="e.g. 992.1" />
            </label>
            <label>
              Car type *
              <select value={car.bodyStyle || ''} onChange={(e) => setCar((c) => ({ ...c, bodyStyle: e.target.value }))} className={styles.select}>
                {CAR_TYPES.map((opt) => (
                  <option key={opt || 'none'} value={opt}>{opt || '— Select —'}</option>
                ))}
              </select>
            </label>
            <label>
              Performance type *
              <select value={car.performanceType || ''} onChange={(e) => setCar((c) => ({ ...c, performanceType: e.target.value }))} className={styles.select}>
                {PERFORMANCE_TYPES.map((opt) => (
                  <option key={opt || 'none'} value={opt}>{opt || '— Select —'}</option>
                ))}
              </select>
            </label>
            <label>
              Trim
              <input value={car.trim} onChange={(e) => setCar((c) => ({ ...c, trim: e.target.value }))} placeholder="e.g. GT3" />
            </label>
            <label>
              Mods
              <input value={car.mods} onChange={(e) => setCar((c) => ({ ...c, mods: e.target.value }))} />
            </label>
            <label>
              Wheels
              <input value={car.wheels} onChange={(e) => setCar((c) => ({ ...c, wheels: e.target.value }))} />
            </label>
            <label>
              Notes
              <input value={car.userNotes} onChange={(e) => setCar((c) => ({ ...c, userNotes: e.target.value }))} />
            </label>
          </div>
        </div>
        <div className={styles.section}>
          <h2>Location & event</h2>
          <label>
            Country
            <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className={styles.select}>
              <option value="">— Select country —</option>
              {COUNTRIES_US_FIRST.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </label>
          <label>
            City
            <input value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>
            Specific location
            <input value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <label>
            Event
            <input value={event} onChange={(e) => setEvent(e.target.value)} />
          </label>
          <label>
            Tag car owner
            <select value={taggedOwnerId} onChange={(e) => setTaggedOwnerId(e.target.value)} className={styles.select}>
              <option value="">None</option>
              {users.filter((u) => u.id !== user?.id).map((u) => (
                <option key={u.id} value={u.id}>{u.displayName} (@{u.username})</option>
              ))}
            </select>
          </label>
        </div>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <button type="submit" className={styles.submit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save changes'}
          </button>
          <button type="button" className={styles.cancel} onClick={() => navigate(`/spot/${id}`)}>
            Cancel
          </button>
          <button type="button" className={styles.delete} onClick={handleDelete} disabled={deleting || submitting}>
            {deleting ? 'Deleting...' : 'Delete spot'}
          </button>
        </div>
      </form>
    </div>
  );
}
